import type { UserDto } from '@api/WhisApi/generated/model/userDto';
import type { AlertDialogAction } from '@components/AlertDialog/AlertDialog';
import { AlertDialog } from '@components/AlertDialog/AlertDialog';
import { Button } from '@components/Button/Button';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { formatDateForLocale } from '@screens/ReportDetail/reportDetail.utils';
import { getCountryLabel } from '@screens/UserManagement/userManagement.utils';
import { ColumnDef } from '@tanstack/react-table';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { XStack } from 'tamagui';

interface UseUserManagementTableColumnsParams {
  onEdit?: (user: UserDto) => void;
  onDelete?: (user: UserDto) => void;
}

export const useUserManagementTableColumns = ({
  onEdit,
  onDelete,
}: UseUserManagementTableColumnsParams): ColumnDef<UserDto>[] => {
  const { t, language } = useAppTranslation();

  return useMemo(
    () => [
      {
        accessorKey: 'firstName',
        header: t('userManagement.columns.firstName'),
        size: 140,
        cell: (info) => info.getValue(),
        enableSorting: true,
      },
      {
        accessorKey: 'lastName',
        header: t('userManagement.columns.lastName'),
        size: 140,
        cell: (info) => info.getValue(),
        enableSorting: true,
      },
      {
        accessorKey: 'email',
        header: t('userManagement.columns.email'),
        size: 250,
        cell: (info) => info.getValue(),
        enableSorting: true,
      },
      {
        accessorKey: 'country',
        header: t('userManagement.columns.country'),
        size: 150,
        enableSorting: true,
        cell: (info) =>
          getCountryLabel(info.row.original.country, language) ||
          t('userManagement.empty.country'),
      },
      {
        accessorKey: 'organization',
        header: t('userManagement.columns.organization'),
        size: 180,
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'role',
        header: t('userManagement.columns.role'),
        size: 140,
        enableSorting: true,
        cell: (info) => t(`userManagement.roles.${String(info.getValue())}`),
      },
      {
        accessorKey: 'createdAt',
        header: t('userManagement.columns.creationDate'),
        size: 140,
        enableSorting: false,
        cell: (info) =>
          formatDateForLocale(info.row.original.createdAt, language) ??
          t('userManagement.empty.date'),
      },
      {
        id: 'actions',
        header: t('userManagement.columns.actions'),
        size: 220,
        cell: (info): ReactElement => {
          const user = info.row.original;

          const deleteActions: AlertDialogAction[] = [
            {
              label: t('buttons.cancel'),
              variant: 'outlined',
              actionType: 'cancel',
            },
            {
              label: t('userManagement.actions.deactivate'),
              variant: 'destructive',
              actionType: 'action',
              onPress: () => onDelete && onDelete(user),
            },
          ];

          return (
            <XStack gap="$2">
              <Button
                variant="outlined"
                title={t('userManagement.actions.edit')}
                onPress={() => onEdit && onEdit(user)}
                size="$2"
                disabled={!user.createdOnAzureFromBack || !onEdit}
              />
              <AlertDialog
                title={t('userManagement.deleteDialog.title')}
                description={t('userManagement.deleteDialog.description')}
                actions={deleteActions}
              >
                <Button
                  variant="outlined-destructive"
                  title={t('userManagement.actions.delete')}
                  size="$2"
                  disabled={!onDelete}
                />
              </AlertDialog>
            </XStack>
          );
        },
        enableSorting: false,
      },
    ],
    [language, onDelete, onEdit, t],
  );
};
